﻿import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { 
  Search, 
  Send, 
  RotateCcw, 
  Check,
  CheckCheck,
  User,
  Settings,
  MessageCircle,
  Loader2,
  LogOut,
  X,
} from 'lucide-react';

import { 
  Message, 
  Conversation, 
  ConnectionStatus,
  ChatState
} from '../types/index';
import { SecuredChatService } from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';

interface ChatInterfaceProps {
  chatService: SecuredChatService;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ chatService }) => {
  const { logout } = useAuth();
  
  // State management - Starting with empty data
  const [state, setState] = useState<ChatState>({
    conversations: [],
    messages: [],
    selectedConversation: '',
    isLoading: true, // Start with loading true
    error: null,
    connectionStatus: 'connecting',
    lastFetch: new Date()
  });

  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyRepliedConversations, setShowOnlyRepliedConversations] = useState(false);
  
  // NEW: Track if we're doing a background refresh (no UI changes)
  const [isBackgroundRefresh, setIsBackgroundRefresh] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedInitially = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // NEW: Refs to save scroll positions
  const conversationsScrollRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollPositions = useRef({
    conversations: 0,
    messages: 0
  });

  // NEW: Ref to track previous message count for auto mark as read
  const previousMessageCountRef = useRef<number>(0);

  // State update helpers
  const updateState = useCallback((updates: Partial<ChatState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // OPTIMIZED: Instant state update for critical actions
  const updateStateInstant = useCallback((updates: Partial<ChatState>) => {
    flushSync(() => {
      setState(prev => ({ ...prev, ...updates }));
    });
  }, []);

  const updateConversations = useCallback((updater: (conversations: Conversation[]) => Conversation[]) => {
    setState(prev => ({ ...prev, conversations: updater(prev.conversations) }));
  }, []);

  // OPTIMIZED: Instant conversation update for mark as read
  const updateConversationsInstant = useCallback((updater: (conversations: Conversation[]) => Conversation[]) => {
    flushSync(() => {
      setState(prev => ({ ...prev, conversations: updater(prev.conversations) }));
    });
  }, []);

  const updateMessages = useCallback((updater: (messages: Message[]) => Message[]) => {
    setState(prev => ({ ...prev, messages: updater(prev.messages) }));
  }, []);

  // NEW: Save scroll positions before any update
  const saveScrollPositions = useCallback(() => {
    if (conversationsScrollRef.current) {
      savedScrollPositions.current.conversations = conversationsScrollRef.current.scrollTop;
    }
    if (messagesScrollRef.current) {
      savedScrollPositions.current.messages = messagesScrollRef.current.scrollTop;
    }
  }, []);

  // NEW: Restore scroll positions after update
  const restoreScrollPositions = useCallback(() => {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      if (conversationsScrollRef.current) {
        conversationsScrollRef.current.scrollTop = savedScrollPositions.current.conversations;
      }
      if (messagesScrollRef.current) {
        messagesScrollRef.current.scrollTop = savedScrollPositions.current.messages;
      }
    });
  }, []);

  // Auto-scroll ONLY when sending new message OR when new messages arrive in selected conversation
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  
  useEffect(() => {
    if (shouldScrollToBottom && !state.isLoading && state.messages.length > 0) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end'
        });
        setShouldScrollToBottom(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToBottom, state.messages, state.isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  // Initial data fetch on component mount
  useEffect(() => {
    if (!hasFetchedInitially.current) {
      console.log('[ChatInterface] Component mounted, fetching initial data');
      hasFetchedInitially.current = true;
      fetchMessages(false); // Initial fetch is not background
    }
  }, []);

  // Auto-refresh setup - UPDATED: 30 seconds interval
  useEffect(() => {
    const service = chatService;
    
    if (!state.isLoading && hasFetchedInitially.current) {
      refreshIntervalRef.current = setInterval(() => {
        console.log('[ChatInterface] Auto-refresh triggered (30s interval)');
        fetchMessages(true); // Auto-refresh is background
      }, service.config.refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [state.isLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Error handling with auto-clear
  const handleError = (error: string, status: ConnectionStatus = 'disconnected') => {
    console.error('[ChatInterface] Error occurred:', error);
    updateState({ error, connectionStatus: status });
    
    setTimeout(() => {
      updateState({ error: null, connectionStatus: 'connected' });
    }, 5000);
  };

  // NEW: Function to detect new messages in selected conversation
  const detectNewMessagesInSelectedConversation = useCallback((newMessages: Message[]): boolean => {
    if (!state.selectedConversation) return false;
    
    const currentSelectedMessages = state.messages.filter(
      msg => msg.conversationId === state.selectedConversation
    );
    
    const newSelectedMessages = newMessages.filter(
      msg => msg.conversationId === state.selectedConversation
    );
    
    // Check if there are new messages (more messages than before)
    if (newSelectedMessages.length > currentSelectedMessages.length) {
      console.log('[ChatInterface] NEW MESSAGES detected in selected conversation:', {
        conversationId: state.selectedConversation,
        previousCount: currentSelectedMessages.length,
        newCount: newSelectedMessages.length,
        newMessages: newSelectedMessages.length - currentSelectedMessages.length
      });
      return true;
    }
    
    // Also check for new message IDs to handle edge cases
    const currentMessageIds = new Set(currentSelectedMessages.map(msg => msg.id));
    const hasNewMessageIds = newSelectedMessages.some(msg => !currentMessageIds.has(msg.id));
    
    if (hasNewMessageIds) {
      console.log('[ChatInterface] NEW MESSAGE IDs detected in selected conversation');
      return true;
    }
    
    return false;
  }, [state.selectedConversation, state.messages]);

  // OPTIMIZED: Mark conversation as read - INSTANT UPDATE
  const markConversationAsRead = useCallback(async (conversationId: string): Promise<void> => {
    const conversation = chatService.getConversationById(
      state.conversations,
      conversationId
    );
    
    if (!conversation || (conversation.isRead && conversation.unreadCount === 0)) {
      console.log('[ChatInterface] Conversation already read or not found:', conversationId);
      return;
    }

    console.log('[ChatInterface] MARKING CONVERSATION AS READ INSTANTLY (JSONB):', conversationId, conversation.phoneNumber);

    // INSTANT UPDATE: Force immediate UI update with flushSync
    updateConversationsInstant(prev => 
      chatService.updateConversationAsRead(prev, conversationId)
    );

    // API call in background (non-blocking)
    try {
      const response = await chatService.markConversationAsRead(
        conversationId,
        conversation.phoneNumber
      );
      console.log('[ChatInterface] Server confirmed conversation marked as read (JSONB):', response);
    } catch (error: any) {
      console.error('[ChatInterface] Server error marking conversation as read (JSONB):', error);
      // Could revert state here if needed, but usually not necessary for read status
    }
  }, [state.conversations, chatService, updateConversationsInstant]);

  // NEW: Auto mark as read when new messages arrive in selected conversation
  useEffect(() => {
    if (!state.selectedConversation || state.isLoading) return;
    
    const currentMessages = chatService.getConversationMessages(state.messages, state.selectedConversation);
    const currentCount = currentMessages.length;
    const previousCount = previousMessageCountRef.current;
    
    // Check if there are new messages (more than before)
    if (currentCount > previousCount && previousCount > 0) {
      console.log('[ChatInterface] 🚨 NEW MESSAGE DETECTED in selected conversation!', {
        conversationId: state.selectedConversation,
        previousCount,
        currentCount,
        newMessages: currentCount - previousCount
      });
      
      // IMMEDIATE mark as read
      console.log('[ChatInterface] ✅ IMMEDIATE mark as read for new message');
      markConversationAsRead(state.selectedConversation);
      
      // Trigger scroll to bottom
      setTimeout(() => {
        console.log('[ChatInterface] 📜 Auto-scroll for new message');
        setShouldScrollToBottom(true);
      }, 100);
      
      // Backup scroll
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end'
        });
      }, 200);
    }
    
    // Update the count for next comparison
    previousMessageCountRef.current = currentCount;
    
  }, [state.messages, state.selectedConversation, state.isLoading, markConversationAsRead]);

  // Fetch messages from server - IMPROVED with background refresh and new message detection
  const fetchMessages = async (isBackground: boolean = false): Promise<void> => {
    console.log('[ChatInterface] fetchMessages called - JSONB system, background:', isBackground);
    console.log('[ChatInterface] Current loading state:', state.isLoading);
    
    // Declare hasNewMessagesInSelected at function scope
    let hasNewMessagesInSelected = false;
    
    try {
      // Save scroll positions BEFORE any state change
      if (isBackground) {
        saveScrollPositions();
        setIsBackgroundRefresh(true);
      }

      // Only show loading state for initial fetch, not background refreshes
      if (!isBackground) {
        updateState({ isLoading: true, connectionStatus: 'connecting', error: null });
      } else {
        // For background refresh, only update connection status
        updateState({ connectionStatus: 'connecting', error: null });
      }

      const response = await chatService.fetchMessages();
      
      console.log('[ChatInterface] SMS get list server response (JSONB system):', response);
      console.log('[ChatInterface] Response structure:', {
        success: response.success,
        hasMessages: !!response.messages,
        messagesCount: response.messages?.length || 0,
        hasConversations: !!response.conversations,
        conversationsCount: response.conversations?.length || 0
      });

      // SMART UPDATE: Only update if data actually changed
      if (response.messages && Array.isArray(response.messages)) {
        const messagesChanged = JSON.stringify(state.messages) !== JSON.stringify(response.messages);
        if (messagesChanged || !isBackground) {
          console.log('[ChatInterface] Updating messages from JSONB:', response.messages.length, 'items');
          
          // NEW: Check for new messages in selected conversation BEFORE updating state
          if (isBackground && state.selectedConversation) {
            hasNewMessagesInSelected = detectNewMessagesInSelectedConversation(response.messages);
            console.log('[ChatInterface] New messages check result:', hasNewMessagesInSelected);
          }
          
          updateState({ messages: response.messages });

          // NEW: If new messages detected in selected conversation, scroll and mark as read IMMEDIATELY
          if (hasNewMessagesInSelected && state.selectedConversation) {
            console.log('[ChatInterface] ✅ NEW MESSAGES in selected conversation - IMMEDIATE ACTION');
            
            // IMMEDIATE mark as read (no delay)
            console.log('[ChatInterface] ✅ IMMEDIATE mark as read for new messages');
            markConversationAsRead(state.selectedConversation);
            
            // Trigger scroll to bottom immediately
            setTimeout(() => {
              console.log('[ChatInterface] 📜 Triggering scroll to bottom for new messages');
              setShouldScrollToBottom(true);
            }, 50);
            
            // Also trigger direct scroll as backup
            setTimeout(() => {
              console.log('[ChatInterface] 📜 Direct scroll to bottom as backup');
              messagesEndRef.current?.scrollIntoView({ 
                behavior: 'smooth',
                block: 'end'
              });
            }, 100);
          }
        }
      }

      // Update conversations if received
      if (response.conversations && Array.isArray(response.conversations)) {
        const conversationsChanged = JSON.stringify(state.conversations) !== JSON.stringify(response.conversations);
        if (conversationsChanged || !isBackground) {
          console.log('[ChatInterface] Updating conversations:', response.conversations.length, 'items');
          updateState({ conversations: response.conversations });
          
          // Auto-select first conversation if none selected and conversations exist
          if (!state.selectedConversation && response.conversations.length > 0) {
            const firstConversationId = response.conversations[0].id;
            console.log('[ChatInterface] Auto-selecting first conversation:', firstConversationId);
            updateState({ selectedConversation: firstConversationId });
            markConversationAsRead(firstConversationId);
          }
        }
      }

      updateState({ 
        lastFetch: new Date(), 
        connectionStatus: 'connected' 
      });

      console.log('[ChatInterface] Data fetch completed successfully (JSONB system)');
      
    } catch (error: any) {
      console.error('[ChatInterface] SMS get list error (JSONB system):', error);
      console.error('[ChatInterface] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      handleError(error.message);
    } finally {
      if (!isBackground) {
        updateState({ isLoading: false });
      }
      
      // Restore scroll positions after background refresh (only if no new messages in selected conversation)
      if (isBackground) {
        setIsBackgroundRefresh(false);
        // Only restore scroll positions if we didn't detect new messages in selected conversation
        if (!hasNewMessagesInSelected) {
          setTimeout(() => {
            restoreScrollPositions();
          }, 50);
        }
      }
      
      console.log('[ChatInterface] Loading state set to false');
    }
  };

  // Send message with improved UX for JSONB system
  const sendMessage = async (): Promise<void> => {
    if (!newMessage.trim() || isSending || !state.selectedConversation) {
      return;
    }

    console.log('[ChatInterface] sendMessage called with JSONB system:', newMessage);
    
    const currentConversation = chatService.getConversationById(
      state.conversations,
      state.selectedConversation
    );

    if (!currentConversation) {
      console.error('[ChatInterface] No conversation found for ID:', state.selectedConversation);
      handleError('Conversation introuvable');
      return;
    }

    console.log('[ChatInterface] Sending message to conversation (JSONB):', currentConversation.phoneNumber);

    setIsSending(true);

    const messageToSend = chatService.createMessage(
      newMessage,
      state.selectedConversation
    );

    console.log('[ChatInterface] Created optimistic message:', messageToSend);

    // Add message to UI immediately for instant feedback
    updateMessages(prev => [...prev, messageToSend]);
    const messageText = newMessage;
    setNewMessage('');
    updateState({ error: null });

    // IMMEDIATE SCROLL: Trigger scroll right after adding the message
    console.log('[ChatInterface] Triggering immediate scroll after sending message');
    setTimeout(() => {
      setShouldScrollToBottom(true);
    }, 50);

    // BACKUP SCROLL: Additional scroll trigger for better reliability
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }, 150);

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);

    try {
      const response = await chatService.sendMessage({
        message: messageText,
        conversationId: state.selectedConversation,
        currentConversation
      });

      console.log('[ChatInterface] Message sent successfully (JSONB system):', response);

      updateMessages(prev => 
        chatService.updateMessageStatus(prev, messageToSend.id, 'delivered')
      );

      updateConversations(prev => 
        chatService.updateConversationLastMessage(
          prev,
          state.selectedConversation,
          messageToSend
        )
      );

      // ADDITIONAL SCROLL: Ensure scroll after status update
      setTimeout(() => {
        setShouldScrollToBottom(true);
      }, 100);

      console.log('[ChatInterface] Refreshing data after successful message send (JSONB system)');
      setTimeout(() => {
        fetchMessages(true); // Background refresh after send
      }, 800);

    } catch (error: any) {
      console.error('[ChatInterface] Error sending message (JSONB system):', error);
      updateMessages(prev => prev.filter(msg => msg.id !== messageToSend.id));
      handleError(error.message);
      setNewMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  // OPTIMIZED: Select conversation with instant feedback
  const selectConversation = useCallback((conversationId: string): void => {
    console.log('[ChatInterface] Selecting conversation (JSONB system):', conversationId);
    
    // INSTANT selection update
    updateStateInstant({ selectedConversation: conversationId });
    
    // Mark as read instantly
    markConversationAsRead(conversationId);

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  }, [markConversationAsRead, updateStateInstant]);

  // Toggle Auto AI
  const toggleAutoAI = async (conversationId: string): Promise<void> => {
    console.log('[ChatInterface] Toggling Auto AI for conversation (JSONB):', conversationId);
    
    const conversation = chatService.getConversationById(
      state.conversations,
      conversationId
    );

    if (!conversation) {
      console.error('[ChatInterface] Conversation not found for Auto AI toggle:', conversationId);
      handleError('Conversation introuvable pour le toggle Auto AI');
      return;
    }

    const previousState = conversation.autoAiEnabled || false;
    console.log('[ChatInterface] Current Auto AI state:', previousState, '-> New state:', !previousState);

    updateConversations(prev =>
      prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, autoAiEnabled: !conv.autoAiEnabled }
          : conv
      )
    );

    try {
      const result = await chatService.toggleAutoAI(state.conversations, conversationId);
      
      if (result.success) {
        console.log('[ChatInterface] Auto AI toggled successfully');
        updateState({ error: null });
      } else {
        console.error('[ChatInterface] Auto AI toggle failed:', result.error);
        
        updateConversations(prev =>
          prev.map(conv =>
            conv.id === conversationId
              ? { ...conv, autoAiEnabled: previousState }
              : conv
          )
        );
        
        handleError(result.error || 'Erreur lors du changement Auto AI');
      }
    } catch (error: any) {
      console.error('[ChatInterface] Auto AI toggle error:', error);
      
      updateConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, autoAiEnabled: previousState }
            : conv
        )
      );
      
      handleError(error.message || 'Erreur lors du changement Auto AI');
    }
  };

  // Handle keyboard shortcuts
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (newMessage.trim() && !isSending && state.selectedConversation) {
        sendMessage();
      }
    }
  };

  // Handle paste event
  const handlePaste = () => {
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }, 0);
  };

  // Get message status icon
  const getMessageStatusIcon = (status?: Message['status']) => {
    switch (status) {
      case 'sent':
        return <Check size={16} className="text-blue-200" />;
      case 'delivered':
        return <CheckCheck size={16} className="text-blue-200" />;
      case 'read':
        return <CheckCheck size={16} className="text-blue-300" />;
      default:
        return null;
    }
  };

  // Format full date and time for messages
  const formatFullDateTime = (timestamp: string): string => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      
      if (isToday) {
        return `Aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (isYesterday) {
        return `Hier ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return date.toLocaleString('fr-FR', { 
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    } catch (error) {
      return '';
    }
  };

  // Format date and time for conversation list
  const formatConversationDateTime = (timestamp: string): string => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      
      if (isToday) {
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      } else if (isYesterday) {
        return 'Hier';
      } else {
        return date.toLocaleDateString('fr-FR', { 
          day: '2-digit',
          month: '2-digit'
        });
      }
    } catch (error) {
      return '';
    }
  };

  // OPTIMIZED: Stable conversation classes with instant transitions for critical actions
  const getConversationClasses = useCallback((conversation: Conversation) => {
    const baseClasses = "w-full text-left flex items-center p-3 rounded-lg shadow-md";
    const isSelected = state.selectedConversation === conversation.id;
    const isUnread = conversation.unreadCount > 0;
    
    // INSTANT transitions for selected state (no animation delay)
    if (isSelected) {
      return `${baseClasses} bg-sky-600 transition-none`;
    }
    
    // NO ANIMATIONS during any refresh to avoid visual glitches
    if (isBackgroundRefresh) {
      return `${baseClasses} ${isUnread ? 'bg-slate-700' : ''}`;
    }
    
    // Normal transitions for non-selected items
    const hoverClasses = 'hover:bg-slate-700 transition-colors duration-150';
    const unreadClasses = isUnread ? 'bg-slate-700' : '';
    
    return `${baseClasses} ${unreadClasses} ${hoverClasses}`;
  }, [state.selectedConversation, isBackgroundRefresh]);

  // Fonction de filtrage des conversations avec la recherche et le filtre de réponses
  const filteredConversations = useMemo(() => {
    let filtered = state.conversations;

    // Filtre par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(conversation => {
        // Recherche par numéro de téléphone
        const phoneMatch = conversation.phoneNumber.toLowerCase().includes(query);
        
        // Recherche dans TOUS les messages de la conversation
        const conversationMessages = chatService.getConversationMessages(state.messages, conversation.id);
        const messageMatch = conversationMessages.some(message => 
          message.text.toLowerCase().includes(query)
        );
        
        return phoneMatch || messageMatch;
      });
    }

    // Filtre pour ne montrer que les conversations avec réponses (plus d'1 message)
    if (showOnlyRepliedConversations) {
      filtered = filtered.filter(conversation => {
        const conversationMessages = chatService.getConversationMessages(state.messages, conversation.id);
        return conversationMessages.length > 1;
      });
    }

    return filtered;
  }, [state.conversations, state.messages, searchQuery, showOnlyRepliedConversations, chatService]);

  // Memoized computed values
  const currentConversationMessages = useMemo(() =>
    chatService.getConversationMessages(state.messages, state.selectedConversation),
    [state.messages, state.selectedConversation]
  );

  const selectedConversationData = useMemo(() =>
    chatService.getConversationById(state.conversations, state.selectedConversation),
    [state.conversations, state.selectedConversation]
  );

  // Statistics
  const unreadCount = chatService.getUnreadConversationsCount(state.conversations);

  // Check if can send message
  const canSendMessage = Boolean(
    selectedConversationData && 
    newMessage.trim() && 
    !isSending && 
    !state.isLoading
  );

  // Manual refresh handler
  const handleManualRefresh = () => {
    fetchMessages(false); // Manual refresh is not background
  };

  // Logout handler
  const handleLogout = () => {
    console.log('[ChatInterface] Logout requested');
    logout();
  };

  // OPTIMIZED: Memoized UnreadBadge component for instant updates
  const UnreadBadge = React.memo(({ count }: { count: number }) => {
    if (count === 0) return null;
    
    return (
      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center transition-none">
        <span className="text-xs text-white font-bold">
          {count > 99 ? '99+' : count}
        </span>
      </div>
    );
  });

  return (
    <div className={`flex h-screen bg-gray-100 overflow-hidden ${isBackgroundRefresh ? 'no-animations' : ''}`}>
      {/* Sidebar */}
      <aside className="w-80 bg-slate-800 text-white flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-xl font-semibold">Oclope.fr</h1>
                <p className="text-sm text-slate-400">
                  Centre de messagerie
                  {unreadCount > 0 && (
                    <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleManualRefresh}
                disabled={state.isLoading}
                className="text-slate-400 hover:text-white transition-colors"
                title="Actualiser"
              >
                <RotateCcw size={20} className={state.isLoading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-red-400 transition-colors"
                title="Se déconnecter"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par numéro ou message..."
                className={`w-full pl-10 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${
                  searchQuery ? 'pr-10' : 'pr-16'
                }`}
              />
              
              {/* Filtre conversations avec réponses - intégré dans la barre de recherche */}
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-slate-400 hover:text-white transition-colors"
                    title="Effacer la recherche"
                  >
                    <X size={16} />
                  </button>
                )}
                
                <button
                  onClick={() => setShowOnlyRepliedConversations(!showOnlyRepliedConversations)}
                  className={`p-1 rounded transition-colors ${
                    showOnlyRepliedConversations 
                      ? 'text-sky-400 bg-sky-500/20' 
                      : 'text-slate-400 hover:text-slate-300'
                  }`}
                  title={showOnlyRepliedConversations ? 'Afficher toutes les conversations' : 'Afficher seulement les conversations avec réponses'}
                >
                  <MessageCircle size={16} />
                </button>
              </div>
            </div>
            
            {(searchQuery || showOnlyRepliedConversations) && (
              <div className="mt-2 text-xs text-slate-400 flex items-center justify-between">
                <span>
                  {filteredConversations.length} résultat{filteredConversations.length !== 1 ? 's' : ''} trouvé{filteredConversations.length !== 1 ? 's' : ''}
                  {searchQuery && showOnlyRepliedConversations && ' (recherche + avec réponses)'}
                </span>
                {showOnlyRepliedConversations && (
                  <span className="text-sky-400 text-xs">✓ Réponses uniquement</span>
                )}
              </div>
            )}
          </div>
        </div>

        
        {/* Error Display */}
        {state.error && (
          <div className="bg-red-900/50 border-b border-red-700/50 px-6 py-3">
            <p className="text-sm text-red-200 font-medium">⚠️ {state.error}</p>
          </div>
        )}

        {/* Loading State */}
        {state.isLoading && state.conversations.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 size={32} className="animate-spin text-slate-400 mb-4 mx-auto" />
              <p className="text-slate-300 font-medium">Chargement des conversations...</p>
              <p className="text-xs text-slate-400 mt-1">Système - Récupération depuis le serveur</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!state.isLoading && state.conversations.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle size={48} className="text-slate-400 mb-4 mx-auto" />
              <p className="text-slate-300 font-medium">Aucune conversation</p>
              <p className="text-xs text-slate-400 mt-1">Les conversations apparaîtront ici</p>
            </div>
          </div>
        )}

        {/* No Search Results */}
        {!state.isLoading && state.conversations.length > 0 && filteredConversations.length === 0 && (searchQuery || showOnlyRepliedConversations) && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Search size={48} className="text-slate-400 mb-4 mx-auto" />
              <p className="text-slate-300 font-medium">Aucun résultat</p>
              <p className="text-xs text-slate-400 mt-1">
                {searchQuery && showOnlyRepliedConversations 
                  ? `Aucune conversation avec réponses ne correspond à "${searchQuery}"`
                  : searchQuery 
                    ? `Aucune conversation ne correspond à "${searchQuery}"`
                    : 'Aucune conversation avec réponses trouvée'
                }
              </p>
              <div className="mt-3 space-x-2">
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-sky-400 hover:text-sky-300 text-sm transition-colors"
                  >
                    Effacer recherche
                  </button>
                )}
                {searchQuery && showOnlyRepliedConversations && (
                  <span className="text-slate-500">•</span>
                )}
                {showOnlyRepliedConversations && (
                  <button
                    onClick={() => setShowOnlyRepliedConversations(false)}
                    className="text-sky-400 hover:text-sky-300 text-sm transition-colors"
                  >
                    Voir toutes
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Conversations List - With scroll position preservation */}
        {!state.isLoading && filteredConversations.length > 0 && (
          <div 
            ref={conversationsScrollRef}
            className="flex-1 overflow-y-auto"
            style={{ scrollBehavior: isBackgroundRefresh ? 'auto' : 'smooth' }}
          >
            <div className="p-4">
              <ul className="space-y-2">
                {filteredConversations.map((conversation: Conversation) => (
                  <li key={conversation.id}>
                    <button
                      onClick={() => selectConversation(conversation.id)}
                      className={getConversationClasses(conversation)}
                    >
                      <div className="flex-shrink-0 mr-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          state.selectedConversation === conversation.id
                            ? 'bg-slate-500'
                            : 'bg-slate-600'
                        }`}>
                          <User size={20} className={state.selectedConversation === conversation.id ? 'text-white' : 'text-slate-300'} />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <p className={`text-sm font-semibold truncate ${
                            state.selectedConversation === conversation.id
                              ? 'text-white'
                              : !conversation.isRead && conversation.unreadCount > 0
                              ? 'text-slate-200'
                              : 'text-slate-200'
                          }`}>
                            {conversation.phoneNumber}
                          </p>
                          <span className={`text-xs ${
                            state.selectedConversation === conversation.id
                              ? 'text-sky-200'
                              : 'text-slate-400'
                          }`}>
                            {formatConversationDateTime(conversation.timestamp)}
                          </span>
                        </div>
                        
                        <p className={`text-xs truncate ${
                          state.selectedConversation === conversation.id
                            ? 'text-sky-100'
                            : 'text-slate-300'
                        }`}>
                          {conversation.lastMessage}
                        </p>
                        
                        <div className="mt-1 flex items-center justify-between">
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                            conversation.autoAiEnabled
                              ? state.selectedConversation === conversation.id
                                ? 'bg-sky-500 text-white'
                                : 'bg-sky-600 text-sky-100'
                              : 'bg-slate-600 text-slate-300'
                          }`}>
                            IA Auto
                          </span>
                          
                          <UnreadBadge count={conversation.unreadCount} />
                        </div>
                      </div>
                      
                      {conversation.isRead && conversation.lastReadTimestamp && (
                        <Check size={16} className={`transition-none ${
                          state.selectedConversation === conversation.id
                            ? 'text-sky-300 ml-2'
                            : 'text-blue-500 ml-2'
                        }`} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <header className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
              <User size={20} className="text-gray-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-800">
              {selectedConversationData?.phoneNumber || 'Sélectionnez une conversation'}
            </h2>
          </div>
          
          {selectedConversationData && (
            <div className="flex items-center space-x-2">
              <Settings size={18} className="text-gray-600" />
              <span className="text-sm text-gray-600">Réponses auto</span>
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={selectedConversationData.autoAiEnabled}
                    onChange={() => toggleAutoAI(selectedConversationData.id)}
                    className="sr-only"
                    disabled={state.isLoading}
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${
                    selectedConversationData.autoAiEnabled ? 'bg-blue-300' : 'bg-gray-300'
                  }`}></div>
                  <div className={`absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    selectedConversationData.autoAiEnabled ? 'transform translate-x-4 bg-blue-500' : ''
                  }`}></div>
                </div>
              </label>
            </div>
          )}
        </header>

        {/* Messages Area - With scroll position preservation */}
        <div 
          ref={messagesScrollRef}
          className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50"
          style={{ scrollBehavior: isBackgroundRefresh ? 'auto' : 'smooth' }}
        >
          {!selectedConversationData ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageCircle size={64} className="text-gray-300 mb-4 mx-auto" />
                <p className="text-gray-600 font-medium text-lg">Sélectionnez une conversation</p>
                <p className="text-gray-400 mt-2">Choisissez une conversation pour commencer à discuter</p>
              </div>
            </div>
          ) : currentConversationMessages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageCircle size={48} className="text-gray-300 mb-4 mx-auto" />
                <p className="text-gray-600 font-medium">Aucun message</p>
                <p className="text-xs text-gray-400 mt-1">Les messages de cette conversation apparaîtront ici</p>
              </div>
            </div>
          ) : (
            <>
              {currentConversationMessages.map((message: Message) => {
                const formattedDateTime = formatFullDateTime(message.timestamp);
                
                return (
                  <div
                    key={message.id}
                    className={`flex ${message.isOutgoing ? 'justify-end' : 'justify-start'} ${
                      isBackgroundRefresh ? '' : 'message-slide-in'
                    }`}
                  >
                    <div className={`p-3 rounded-lg max-w-lg shadow ${
                      message.isOutgoing
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {message.text}
                      </p>
                      <div className={`flex items-center justify-end mt-1 ${
                        message.isOutgoing ? 'text-blue-200' : 'text-gray-500'
                      }`}>
                        <p className="text-xs mr-1">
                          {formattedDateTime}
                        </p>
                        {message.isOutgoing && getMessageStatusIcon(message.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <footer className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              onPaste={handlePaste}
              placeholder={selectedConversationData ? "Écrivez votre message... (Ctrl+Entrée pour envoyer)" : "Sélectionnez une conversation"}
              rows={1}
              disabled={!selectedConversationData || state.isLoading}
              className="flex-1 p-2 bg-transparent text-gray-800 placeholder-gray-500 focus:outline-none resize-none disabled:opacity-50 overflow-hidden"
              style={{ minHeight: '40px', maxHeight: '120px' }}
              maxLength={4000}
            />
            <button
              onClick={sendMessage}
              disabled={!canSendMessage}
              className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-400">
              <span className="font-medium">Ctrl+Entrée</span> pour envoyer • <span className="font-medium">Entrée</span> pour une nouvelle ligne
            </p>
            {newMessage.length > 3800 && (
              <p className="text-xs text-amber-600 font-medium">
                {4000 - newMessage.length} caractères restants
              </p>
            )}
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ChatInterface;